import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { CustomButton } from '../Button/Button';
import ClipsList from '../ClipsList/ClipsList';
import Icon from '../Icon/Icon';
import InviteModalContent from '../InviteModalContent/InviteModalContent';
import Modal from '../Modal';
import NameEditComponent from '../NameEditComponent/NameEditComponent';
import GeneratedSceneItem from '../GeneratedSceneItem/GeneratedSceneItem';
import StoryboardSceneMenu from '../StoryboardSceneMenu/StoryboardSceneMenu';
import styles from './GeneratedScenesEditor.module.scss';
import cx from 'classnames';
import StoryboardRightSidebar from '../StoryboardRightSidebar/StoryboardRightSidebar';
import CmsAPI from '../../../common/apis/CmsAPI';
import { useHistory, useLocation } from 'react-router-dom';
import { useUserContext } from '../../context/UserContext';
import ManageTeamModalContent from '../ManageTeamModalContent/ManageTeamModalContent';
import StoryboardMenu from '../StoryboardMenu/StoryboardMenu';
import StoryboardAssistantModal from '../../features/Storyboard/components/StoryboardAssistantModal/StoryboardAssistantModal';
import StoryboardSpinner from '../../features/Storyboard/components/StoryboardSpinner/StoryboardSpinner';
import VormatsAPI from '../../../common/apis/VormatsAPI';
import { ToastNotification } from '../Toast/Toast';
import RadioButton from '../RadioButton/RadioButton';
import { useLanguageContext } from '../../context/LanguageContext';
import queryString from 'query-string';
import { STORY_USER_RELATION } from '../../constants/UserRoles';
import AvatarModal from 'engage/features/Editor/components/AvatarModal/AvatarModal';
import VideoService from 'engage/service/VideoService';
import StorySchedule from 'engage/features/Storyboard/components/StorySchedule/StorySchedule';
import * as Sentry from '@sentry/react';
import { useStoryboardContext } from '../../context/StoryboardContext';
import { GeneratedScenesHeader } from 'engage/features/Storyboard/components/GeneratedScenesHeader/GeneratedScenesHeader';
import { ORIENTATIONS } from 'engage/constants/projectCreatorConsts';
import { ScenePlayer } from 'engage/features/Storyboard/components/ScenePlayer/ScenePlayer';
import { useSceneMediaUpdate } from 'engage/features/Storyboard/hooks/useSceneMediaUpdate';
import UploadClipModal from 'engage/features/Editor/components/UploadClipModal/UploadClipModal';
import UploadClipTab from '../UploadClipTab/UploadClipTab';
import ClipsProvider from 'engage/context/provider/ClipsProvider';
import AddClipModal from '../AddClipModal/AddClipModal';
import AddClipContent from '../AddClipContent/AddClipContent';
import useFixedCardMenu from 'engage/features/Editor/hooks/useFixedCardMenu';
import TooltipButton from '../TooltipButton/TooltipButton';
import InstallExtensionModal from '../InstallExtensionModal/InstallExtensionModal';

const commentsType = 3;

const GeneratedScenesEditor = ({
  videoAuthor,
  applyAssistantChanges,
  pushJobs
}) => {
  const { t } = useTranslation();
  const [scroll, setScroll] = useState(false);
  const [showInviteModal, setInviteModal] = useState(null);
  const [showManageTeamModal, setManageTeamModal] = useState(false);
  const [showStoryboardAssistant, setShowStoryboardAssistant] = useState(false);

  const [expand, setExpand] = useState(false);
  const [animateExpand, setAnimateExpand] = useState(false);
  const [comments, setComments] = useState(null);
  const [isCommentsLoading, setIsCommentsLoading] = useState(true);
  const [menuForScene, setMenuForScene] = useState(null);
  const [storyboardMenu, setStoryboardMenu] = useState({ flag: false });
  const [assistantLoading, setAssistantLoading] = useState(false);
  const [assistantReply, setAssistantReply] = useState(null);
  const [comparisonMode, setComparisonMode] = useState(false);
  const [avatarSelectionModal, setAvatarSelectionModal] = useState({ show: false });
  const [isStoryScheduleOpen, setIsStoryScheduleOpen] = useState(false);
  const [isShowExtensionModal, setIsShowExtensionModal] = useState(false);
  const [showScrollIndicator, setShowScrollIndicator] = useState(false);
  const scrollIndicatorTimeoutRef = useRef(null);
  const hasShownScrollIndicator = useRef(false);

  const hideScrollIndicator = useCallback(() => {
    setShowScrollIndicator(false);
  }, []);

  const [showPlayer, setShowPlayer] = useState(false);

  const { showMenu, menuCoordinates, menuRef, toggleShowMenu, onOpenMenu } = useFixedCardMenu();

  const {
    videoId,
    storyRole,
    scenes,
    loading,
    assignments,
    storyPermissions: permissions,
    userPermissions,
    currentScene,
    projectName,
    updateProjectName,
    duplicateScene,
    deleteScene,
    selectScene,
    inviteForScenes,
    changeScene,
    addScene,
    moveScene,
    manageTeam,
    remindUser,
    copyProjectLink,
    reviseRequest,
    changeUserPermissions,
    checkSceneJobs,
    copyScenesIntoStory,
  } = useStoryboardContext();

  const {
    cancelRecording,
    isRecording,
    startRecording,
    showUploadingModal,
    uploadClipInfo,
    cancelUploading,
    startUploading,
    uploadingDone,
    showAvatarModal,
    avatarSceneId,
    startAvatar,
    cancelAvatar,
    selectAvatar,
    showLibraryModal,
    startSelectFromLibrary,
    cancelSelectFromLibrary,
    selectionDone,
  } = useSceneMediaUpdate({
    storyId: videoId,
    scenes,
    currentScene,
    addScene,
    updateScene: changeScene,
    checkSceneJobs,
    deleteScene,
    copyScenesIntoStory,
    onExtensionInstall: () => setIsShowExtensionModal(true)
  });

  const [storySchedule, setStorySchedule] = useState({});

  const usersArray = useMemo(() => {
    let users = scenes
      .filter(item => Boolean(item.author) && item?.author?.username !== videoAuthor.username)
      .map(item => item.author)
      .filter((user, index, array) => array.findIndex(item => item.username === user.username) === index);

    const permission = permissions
      .filter(item => Boolean(item.username) && item?.username !== videoAuthor.username)
      .map(item => {
        const { avatar, username, email, name } = item;
        return {
          username,
          email,
          name,
          avatar,
        };
      });

    if (permission.length > 0) {
      users = users
        .concat(permission)
        .filter((user, index, array) => array.findIndex(item => item.username === user.username) === index);
    }
    // Sort users by name
    return users.sort((a, b) => {
      if (a.name.toLowerCase() < b.name.toLowerCase()) {
        return -1;
      }
      if (a.name.toLowerCase() > b.name.toLowerCase()) {
        return 1;
      }
      return 0;
    });
  }, [scenes]);

  const [selected, setSelected] = useState(false);

  const { language } = useLanguageContext();

  const assistantAbortController = useRef(null);

  const [requestIsScriptReady, setRequestIsScriptReady] = useState({});

  const videoOrientation = useMemo(() => {
    const isHasLandscape = scenes.some(({ orientation }) => orientation === ORIENTATIONS.LANDSCAPE.type);

    if (isHasLandscape) {
      return ORIENTATIONS.LANDSCAPE;
    }
    const isHasPortrait = scenes.some(({ orientation }) => orientation === ORIENTATIONS.PORTRAIT.type);

    if (isHasPortrait) {
      return ORIENTATIONS.PORTRAIT;
    }

    return ORIENTATIONS.SQUARE;
  }, [scenes]);

  const assistantHeaderIsDiff = useMemo(() => comparisonMode && assistantReply.name !== projectName, [
    comparisonMode,
    assistantReply,
    projectName,
  ]);

  const mixedSelection = useMemo(() => {
    if (selected) {
      let mixedScenes = !selected?.scenes?.every(item =>
        Object.entries(item).every(([key, value]) => {
          return key === 'id' || value;
        })
      );
      if (!mixedScenes) {
        mixedScenes = selected?.scenes.length !== assistantReply?.scenes.length;
      }
      return mixedScenes || !selected?.name;
    }
    return null;
  }, [selected, assistantReply]);

  const wrapperRef = useRef();


  const history = useHistory();
  const location = useLocation();

  const {
    user,
  } = useUserContext();

  const onTogglePlayer = () => setShowPlayer(prevState => !prevState);

  const currentSceneHasVideo = useMemo(() => {
    const currentSceneData = scenes[Number(currentScene)];
    return currentSceneData && currentSceneData.video;
  }, [scenes, currentScene]);

  const handleScrollTooltipClick = () => {
    hideScrollIndicator();
    if (scrollIndicatorTimeoutRef.current) {
      clearTimeout(scrollIndicatorTimeoutRef.current);
      scrollIndicatorTimeoutRef.current = null;
    }
  };

  const onSortEnd = (...args) => {
    moveScene(...args);
  };

  const scrollHandle = useCallback(() => {
    setScroll(prevState => (prevState ? prevState : !prevState));
  }, [scroll]);

  const isEditingOptionsEnabled = useMemo(() => {
    if (!userPermissions.updateStory) {
      return storyRole !== STORY_USER_RELATION.VIEWER.TYPE;
    }
    return true;
  }, [userPermissions, storyRole, videoAuthor, user]);

  const onSelect = (sceneId, key, value) => {
    setSelected(prevState => {
      if (prevState.scenes.find(item => item.id === sceneId)) {
        // If scene should be removed from final result filter it out
        if (key === null) {
          return {
            ...prevState,
            scenes: prevState.scenes.filter(item => item.id !== sceneId),
          };
        }
        return {
          ...prevState,
          scenes: prevState.scenes.map(scene => {
            if (sceneId === scene.id) {
              if (key) {
                return { ...scene, [key]: value };
              }
              return { id: scene.id, name: value, script: value, narrative: value };
            }
            return scene;
          }),
        };
      } else {
        // Scene should be added to final result
        return { ...prevState, scenes: [...prevState.scenes, { id: sceneId, name: value, script: value, narrative: value }] };
      }
    });
  };

  const isKeySceneInArray = (sceneId, array, key) => {
    return array.find((item) => item[key]?.id === sceneId) !== undefined;
  };

  const scenesList = useMemo(() => {
    if (comparisonMode) {
      const iterations = Math.max(assistantReply.scenes.length, scenes.length);
      const scenesToShow = [];
      for (let i = 0; i < iterations; i++) {
        // Start from 0 scene (assuming assistantReply.scenes are filtered by sort)
        const assistantScene =
          i < assistantReply.scenes.length ? assistantReply.scenes[i] : null;
        const originalScene = i < scenes.length ? scenes[i] : null;

        if (assistantScene?.id === originalScene?.id) {
          // Position of the scene was not changed
          scenesToShow.push({ originalScene, assistantScene });
          // Handle cases when same sort has different scenes
        } else {
          // Check if current sort old scene exists in assistant reply
          const originalSceneInAssistantResponse = originalScene
            ? assistantReply.scenes.find((scene) => scene.id === originalScene.id)
            : null;
          const assistantSceneInOriginal = assistantScene
            ? scenes.find((scene) => scene.id === assistantScene.id)
            : null;

          if (originalScene && !originalSceneInAssistantResponse) {
            // Scene was removed by the assistant
            scenesToShow.push({
              originalScene,
              assistantScene: null,
            });
          }

          // Check is current sort assistant scene were existed before in original scenes
          if (assistantSceneInOriginal) {
            const isSceneAlreadyAdded = isKeySceneInArray(
              assistantSceneInOriginal.id,
              scenesToShow,
              "originalScene"
            );
            // Scene is not removed by assistant, sort is changed, add
            // scene to the end result with new sort value
            !isSceneAlreadyAdded &&
              scenesToShow.push({
                originalScene: assistantSceneInOriginal,
                assistantScene,
              });
          } else if (assistantScene && !assistantSceneInOriginal) {
            // Scene was added by assistant
            scenesToShow.push({
              originalScene: null,
              assistantScene,
            });
          }
        }
      }
      return scenesToShow?.map(({ originalScene, assistantScene }) => {
        const itemAssistantSelected = selected?.scenes?.find(scene => scene.id === assistantScene?.id) ?? null;
        return (
          <li key={assistantScene?.id ?? originalScene?.id} className={styles.narrativeListItem}>
            <GeneratedSceneItem
              item={originalScene}
              assistantItem={assistantScene}
              onChange={changeScene}
              userPermissions={userPermissions}
              comparisonMode={comparisonMode}
              onSelectOption={onSelect}
              assistantSelected={itemAssistantSelected}
            />
          </li>
        );
      });
    }
    return scenes.map(item => {
      const sameIdScene = assistantReply?.scenes.find(scene => scene.id === item.id);
      const itemAssistantSelected = selected?.scenes?.find(scene => scene.id === item.id) ?? null;
      return (
        <li key={item.id} className={styles.narrativeListItem}>
          <GeneratedSceneItem
            item={item}
            assistantItem={sameIdScene}
            onChange={changeScene}
            userPermissions={userPermissions}
            comparisonMode={comparisonMode}
            onSelectOption={onSelect}
            assistantSelected={itemAssistantSelected}
          />
        </li>
      );
    });
  }, [comparisonMode, scenes, assistantReply?.scenes, changeScene, userPermissions, selected]);

  const toggleShowStoryboardMenu = useCallback(
    ({ clientX, clientY } = {}) => {
      setStoryboardMenu(prevState => {
        if (prevState.flag) {
          return { flag: false };
        }

        return {
          flag: true,
          left: clientX,
          top: clientY,
        };
      });
    },
    [setStoryboardMenu]
  );

  const onDuplicateCard = useCallback(() => {
    duplicateScene(showMenu.id);
  }, [showMenu]);

  const onDeleteCard = useCallback(() => {
    deleteScene(showMenu.id);
  }, [showMenu, deleteScene]);

  const onSendReminder = useCallback(() => {
    remindUser(menuForScene.author.username);
  }, [showMenu, remindUser]);

  const onRequestRevise = useCallback(() => {
    reviseRequest(menuForScene.author.username, menuForScene.author.name, menuForScene.id);
  }, [showMenu]);

  const onUseAvatarOpen = useCallback(() => {
    setRequestIsScriptReady({ show: true, sceneId: menuForScene?.id ?? null, ...(menuForScene?.id ? { name: scenes.find(item => item.id == menuForScene?.id)?.name } : {}) });
  }, [showMenu, scenes]);

  const onConfirmScript = useCallback(() => {
    setAvatarSelectionModal({ show: true, sceneId: requestIsScriptReady?.sceneId });
    setRequestIsScriptReady({});
  }, [requestIsScriptReady]);

  const handleSaveSchedule = useCallback(async (start, end, notes, notifications = []) => {
    setIsStoryScheduleOpen(false);
    const payload = {
      ...(start == new Date(storySchedule?.start).getTime() ? {} : { start: new Date(start).toISOString() }),
      ...(end ? { end: new Date(end).toISOString() } : storySchedule?.end ? { end: null } : {}),
      ...(notes ? { notes } : {}),
    }
    try {
      if (!storySchedule?.id) {
        payload.notifications = notifications;
        const schedule = await VideoService.createStorySchedule(videoId, payload);
        setStorySchedule(schedule);
        return;
      }
      const isNotificationsChanged = notifications?.find(item => item.hasOwnProperty('tempId')) || JSON.stringify(storySchedule.notifications?.map(item => item.id)) !== JSON.stringify(notifications?.map(item => item.id)) || JSON.stringify(storySchedule.notifications?.map(item => item.offset)) !== JSON.stringify(notifications?.map(item => item.offset));
      // Separate notifications that should be created and that should be updated
      const schedule = await VideoService.updateStorySchedule(videoId, payload);
      setStorySchedule(schedule);
      if (isNotificationsChanged) {
        const updatedNotifications = await VideoService.updateScheduleNotifications(videoId, notifications);
        setStorySchedule(prevState => ({ ...prevState, notifications: updatedNotifications }));
      }
    } catch (e) {
      console.log('Error while creating schedule', e);
      ToastNotification({ message: t('storyboard.editor.schedule.error'), closeButton: true });
    }
  }, [storySchedule]);

  const handleRemoveStorySchedule = async () => {
    setIsStoryScheduleOpen(false);
    await VideoService.removeSchedule(videoId);
    setStorySchedule({});
  }

  const settingChangedFormMenu = settings => {
    setMenuForScene(prevState => ({ ...prevState, permissions: { ...prevState.permissions, ...settings } }));
  };

  const openStoryboardAssistant = () => {
    setShowStoryboardAssistant(true);
  };

  const handleCancelChanges = () => {
    setComparisonMode(false);
    setAssistantReply(null);
    setTimeout(() => {
      window.scrollTo({
        top: 0,
        behavior: 'smooth',
      });
      history.replace({ search: '' });
    }, 100);
  };

  const onApplyAssistantChanges = useCallback(async () => {
    setAssistantLoading(true);
    let preparedData = assistantReply;
    if (mixedSelection) {
      preparedData = { name: selected?.name ? assistantReply.name : projectName };
      preparedData.scenes = selected?.scenes
        ?.map(scene => {
          const resultedScene = {};
          const findFromAssistant = scene.name || scene.script || scene.narrative;
          const findFromOriginal = !scene.name || !scene.script || !scene.narrative;
          const assistantScene = findFromAssistant ? assistantReply.scenes.find(item => item.id === scene.id) : {};
          const originalScene = findFromOriginal ? scenes.find(item => item.id === scene.id) : {};
          ['name', 'narrative', 'script'].forEach(key => {
            if (scene[key] && assistantScene) {
              resultedScene[key] = assistantScene[key];
            }
            if (!scene[key] && originalScene) {
              resultedScene[key] = originalScene[key];
            }
          });
          if (!Object.keys(resultedScene).length) {
            return null;
          }
          return { ...resultedScene, id: scene.id };
        })
        .filter(item => item !== null);
    }
    await applyAssistantChanges(preparedData);
    setComparisonMode(false);
    setAssistantLoading(false);
    setAssistantReply(null);
    setTimeout(() => {
      window.scrollTo({
        top: 0,
        behavior: 'smooth',
      });
      history.replace({ search: '' });
    }, 100);
  }, [assistantReply, applyAssistantChanges, mixedSelection, selected]);

  const handleOpenInvite = useCallback(() => {
    closeManageTeamModal();
    setInviteModal(showMenu?.id ?? '');
  }, [showMenu]);

  const closeInviteModal = () => {
    setInviteModal(null);
  };

  const closeManageTeamModal = () => {
    setManageTeamModal(false);
  };

  const closeStoryboardAssistant = () => {
    setShowStoryboardAssistant(false);
  };

  const handleAssistantRequest = useCallback(
    async request => {
      setShowStoryboardAssistant(false);
      history.push({
        pathname: location.pathname,
        search: 'assistant=true',
      });
      setAssistantLoading(true);
      try {
        assistantAbortController.current = new AbortController();
        const response = await VormatsAPI.editStoryboard(videoId, request, language.lng.substring(0, 2), assistantAbortController.current.signal);
        if (response.scenes.length === scenes.length && response.name === projectName) {
          const noChanges = response.scenes.every(item => {
            const originalScene = scenes.find(scene => scene.id === item.id);
            return item?.sort === originalScene?.sort && ['name', 'script', 'narrative'].every(key => originalScene[key] === item[key]);
          });
          if (noChanges) {
            setAssistantLoading(false);
            ToastNotification({ message: t('storyboard.editor.assistant.no_changes'), closeButton: true, timeout: 10000 });
            return;
          }
        }
        setAssistantReply({ ...response, scenes: response.scenes.sort((a, b) => a.sort - b.sort) });
        const generateSelected = {
          name: true,
          scenes: response.scenes.map(item => {
            const existedScene = scenes.find(scene => scene.id === item.id);
            const resultObject = { id: item.id };
            if (existedScene) {
              ['name', 'script', 'narrative'].forEach(key => {
                if (existedScene[key] !== item[key]) {
                  resultObject[key] = true;
                }
              });
            } else {
              resultObject.name = true;
              resultObject.narrative = true;
              resultObject.script = true;
            }
            return resultObject;
          }),
        };
        setSelected(generateSelected);
        setComparisonMode(true);
        setAssistantLoading(false);
        setExpand(false);
      } catch (e) {
        console.log('Error while assistant request', e);
        if (!e.code === "ERR_CANCELED") {
          setAssistantLoading(false);
          history.replace({ search: '' });
          ToastNotification({ message: t('storyboard.steps.generating_error') });
        }
      }
    },
    [scenes, videoId, language]
  );

  const handleAvatarSelection = useCallback(async (selection) => {
    const generationArray = [];
    if (selection.length == 1 && !selection[0].hasOwnProperty('sceneId')) {
      // Use selected avatar and voice to generate scenes that has script
      const { avatarId, voiceId } = selection[0];
      generationArray.push(...scenes.filter(scene => scene.script.length).map(scene => ({ sceneId: scene.id, avatarId, voiceId })));
    } else {
      generationArray.push(...selection);
    }
    const jobs = await VideoService.generateBatchAvatarClips(videoId, generationArray);
    pushJobs(jobs.map(job => job.id));
  }, [scenes]);

  const toggleExpand = () => {
    setAnimateExpand(true);
    setTimeout(() => {
      setExpand(prevState => !prevState);
      setAnimateExpand(false);
    }, 250);
  };

  const loadComments = async () => {
    try {
      setIsCommentsLoading(true);
      const { result } = await CmsAPI.getComments(videoId, commentsType);
      setComments(result || []);
    } catch (err) {
      console.log(err);
      setComments([]);
    } finally {
      setIsCommentsLoading(false);
    }
  };

  const onAddEmptyClip = (index, before) => {
    addScene(index, before, videoOrientation?.type);
  };

  const handleSelectName = (assistant = true) => e => {
    const name = assistant ? e.currentTarget.checked : !e.currentTarget.checked;
    setSelected(prevState => ({ ...prevState, name }));
  };

  const handleAssistantCanceled = () => {
    assistantAbortController?.current?.abort();
    setAssistantLoading(false);
    setTimeout(() => {
      history.replace({ search: '' });
    }, 100)
  }

  useEffect(() => {
    scroll &&
      setTimeout(() => {
        setScroll(false);
      }, 1000);
  }, [scroll]);

  useEffect(() => {
    window.addEventListener('scroll', scrollHandle);
    return () => {
      window.removeEventListener('scroll', scrollHandle);
    };
  }, []);

  useEffect(() => {
    if (hasShownScrollIndicator.current) return;
    if (comparisonMode) return;
    const listContainer = document.querySelector('[data-testid="scripts-section"]');
    if (listContainer) {
      const containerRect = listContainer.getBoundingClientRect();
      const windowHeight = window.innerHeight;
      const isContentBelow = containerRect.top > windowHeight * 0.8;
      if (isContentBelow) {
        setShowScrollIndicator(true);
        hasShownScrollIndicator.current = true;
      }
    }
    // Clean up on unmount
    return () => {
      if (scrollIndicatorTimeoutRef.current) {
        clearTimeout(scrollIndicatorTimeoutRef.current);
        scrollIndicatorTimeoutRef.current = null;
      }
    };
  }, [comparisonMode, scenes]);

  useEffect(() => {
    if (!showScrollIndicator) return;
    const onScroll = () => {
      const listContainer = document.querySelector('[data-testid="scripts-section"]');
      if (listContainer) {
        const rect = listContainer.getBoundingClientRect();
        if (rect.top < window.innerHeight && rect.bottom > 0) {
          hideScrollIndicator();
        }
      }
    };
    window.addEventListener('scroll', onScroll);
    return () => {
      window.removeEventListener('scroll', onScroll);
    };
  }, [showScrollIndicator, hideScrollIndicator]);

  useEffect(() => {
    if (!showScrollIndicator) return;
    scrollIndicatorTimeoutRef.current = setTimeout(() => {
      hideScrollIndicator();
      scrollIndicatorTimeoutRef.current = null;
    }, 4000);
    return () => {
      if (scrollIndicatorTimeoutRef.current) {
        clearTimeout(scrollIndicatorTimeoutRef.current);
        scrollIndicatorTimeoutRef.current = null;
      }
    };
  }, [showScrollIndicator, hideScrollIndicator]);

  useEffect(() => {
    const loadSchedule = async () => {
      try {
        const response = await VideoService.getStorySchedule(videoId);
        setStorySchedule(response);
      } catch (e) {
        Sentry.captureException(e);
        console.log('Error while loading schedule', e);
      }
    }


    loadComments();

    loadSchedule();

    const parsed = queryString.parse(location.search);
    if (parsed.assistant) {
      history.replace({ search: '' });
    }
  }, []);

  useEffect(() => {
    if (!showMenu?.flag && menuForScene !== null) {
      // Check if settings changed
      const sceneToChange = scenes.find(item => item.id === menuForScene?.id);
      if (
        sceneToChange !== undefined &&
        (sceneToChange?.permissions?.read !== menuForScene?.permissions?.read ||
          sceneToChange?.permissions?.update !== menuForScene?.permissions?.update)
      ) {
        changeUserPermissions(menuForScene.author.username, menuForScene?.id, menuForScene.permissions);
        setMenuForScene(null);
      }
    }
  }, [showMenu]);

  useEffect(() => {
    let unblock =
      comparisonMode || assistantLoading
        ? history.block(tx => {
          // Navigation was blocked! Let's show a confirmation dialog
          // so the user can decide if they actually want to navigate
          // away and discard changes they've made in the current page.
          if (confirm(t('storyboard.editor.assistant.confirm_leave_page'))) {
            // Unblock the navigation.
            setComparisonMode(false);
            setAssistantLoading(false);
            setAssistantReply(null);
            return true;
          } else {
            return false;
          }
        })
        : history.block(() => {
          return true;
        });

    return () => unblock();
  }, [comparisonMode, assistantLoading]);

  useEffect(() => {
    const onReloadEvent = event => {
      const e = event || window.event;
      // Cancel the event
      e.preventDefault();
      if (e) {
        e.returnValue = ''; // Legacy method for cross browser support
      }
      return ''; // Legacy method for cross browser support
    };

    if (comparisonMode || assistantLoading) {
      window.addEventListener('beforeunload', onReloadEvent);
    } else {
      window.removeEventListener('beforeunload', onReloadEvent);
    }

    return () => window.removeEventListener('beforeunload', onReloadEvent);
  }, [comparisonMode, assistantLoading]);

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (scrollIndicatorTimeoutRef.current) {
        clearTimeout(scrollIndicatorTimeoutRef.current);
        scrollIndicatorTimeoutRef.current = null;
      }
    };
  }, []);

  if (scenes?.length == 0) {
    return null;
  }

  return (
    <>
      <div ref={wrapperRef} className={cx(styles.wrapper, { [styles.blockScroll]: assistantLoading })}>
        {comparisonMode ? (
          <div className={cx(styles.assistantHeader, { [styles.scrolled]: window.scrollY > 0 })}>
            <CustomButton
              label={t('storyboard.editor.assistant.keep_old')}
              variant="secondary"
              onClick={handleCancelChanges}
              disabled={assistantLoading}
            />
            <CustomButton
              label={t(mixedSelection ? 'storyboard.editor.assistant.save_selected' : 'storyboard.editor.assistant.save_all_new')}
              variant="basic"
              onClick={onApplyAssistantChanges}
              disabled={assistantLoading}
            />
          </div>
        ) : null}
        <div className={cx(styles.container, { [styles.fullWidth]: comparisonMode })}>
          <GeneratedScenesHeader
            isEditingOptionsEnabled={isEditingOptionsEnabled}
            comparisonMode={comparisonMode}
            style={comparisonMode ? { opacity: 0 } : {}} />
          {comparisonMode ? <div className={styles.projectNameContainer}>
            <div className={styles.nameWithSelection}>
              {assistantHeaderIsDiff && (
                <RadioButton
                  variant="primary"
                  checked={!selected?.name}
                  onChange={handleSelectName(false)}
                  className={styles.radioBtn}
                />
              )}
              <NameEditComponent
                className={styles.projectName}
                name={projectName}
                handleSaveName={updateProjectName}
                disableEditing={comparisonMode}
              />
            </div>
            {assistantHeaderIsDiff && (
              <div className={styles.nameWithSelection}>
                <RadioButton
                  variant="secondary"
                  checked={selected?.name}
                  onChange={handleSelectName(true)}
                  className={styles.radioBtn}
                />
                <NameEditComponent name={assistantReply?.name} disableEditing={true} variant="secondary" />
              </div>
            )}
          </div> : null}
          {!comparisonMode && (
            <div className={styles.clipsContainer}>
              <ScenePlayer
                isRecording={isRecording}
                cancelRecording={cancelRecording}
                startRecording={startRecording}
                startUploading={startUploading}
                startAvatar={startAvatar}
                collapse={!showPlayer}
              />
              <div className={styles.clipsHeader}>
                <TooltipButton
                  variant='link'
                  onClick={onTogglePlayer}
                  className={styles.togglePlayerButton}
                  classNameTooltip={styles.togglePlayerTooltip}>
                  <span>
                    {showPlayer
                      ? t('storyboard.editor.hide_player')
                      : currentSceneHasVideo
                        ? t('storyboard.editor.show_player')
                        : t('storyboard.editor.record_or_upload')
                    }
                  </span>
                </TooltipButton>
              </div>
              <ClipsList
                saving={loading}
                cards={scenes}
                currentVideoPart={currentScene}
                handleDotsClick={userPermissions.updateStory && onOpenMenu}
                handleSelectClip={selectScene}
                showMenu={showMenu}
                onSortEnd={onSortEnd}
                onAddEmptyClip={onAddEmptyClip}
                allowTransitions={false}
                isDraft={true}
                allowOpenExtension={false}
                allowAddClip={userPermissions.updateStory}
                userPermissions={userPermissions}
                className={styles.clipsListContainer}
                onUploadClip={userPermissions.updateStory ? (index, before) => startUploading(null, index, before) : null}
                handleRecordScene={userPermissions.updateStory ? ({ index, before }) => startRecording(null, index, before) : null}
                onAddClip={startSelectFromLibrary}
              />
            </div>
          )}

          {!comparisonMode && (
            <div className={styles.expandContainer}>
              <CustomButton variant="link" className={styles.expandButton} onClick={toggleExpand} disabled={scenes.length === 1}>
                <span className={cx(styles.expandText, { [styles.animate]: animateExpand })}>
                  {t(expand ? 'storyboard.editor.expand_script' : 'storyboard.editor.collapse_script')}
                </span>
                <Icon src={'showHideArrow'} className={cx(styles.arrowIcon, { [styles.expand]: expand })} />
              </CustomButton>
            </div>
          )}

          {showScrollIndicator && !comparisonMode && (
            <div className={styles.scrollIndicator} onClick={handleScrollTooltipClick}>
              <div className={styles.scrollIndicatorContent}>
                <span className={styles.scrollText}>{t('storyboard.editor.scroll_to_see_scripts')}</span>
              </div>
            </div>
          )}
          <div className={styles.listContainer} data-testid="scripts-section">
            {expand ? (
              <GeneratedSceneItem
                key={scenes[Number(currentScene)]?.id}
                item={scenes[Number(currentScene)]}
                onChange={changeScene}
                userPermissions={userPermissions}
                isCollapsed={true}
              />
            ) : (
              <ul className={styles.narrativesWrapper} onScroll={scrollHandle}>
                {scenesList}
              </ul>
            )}
          </div>
        </div>
        {!comparisonMode && (
          <StoryboardRightSidebar
            loadComments={loadComments}
            comments={comments}
            isCommentsLoading={isCommentsLoading}
            isEditingOptionsEnabled={isEditingOptionsEnabled}
            storySchedule={storySchedule}
            videoOrientation={videoOrientation}
            onSchedule={() => setIsStoryScheduleOpen(true)}
            usersArray={usersArray}
            onViewTeam={() => setManageTeamModal(true)}
            onInvite={handleOpenInvite}
            openStoryboardAssistant={openStoryboardAssistant}
            onUseAvatarOpen={onUseAvatarOpen}
          />
        )}
        <Modal className={styles.modalWrapper} open={showInviteModal !== null} onClose={closeInviteModal}>
          <InviteModalContent
            id={showInviteModal}
            onClose={closeInviteModal}
            onSubmit={inviteForScenes}
            scenes={scenes}
            selectedScene={scenes[Number(currentScene)]}
            videoAuthor={videoAuthor}
            assignments={assignments}
            permissions={permissions}
          />
        </Modal>
        <Modal className={styles.modalWrapper} open={showManageTeamModal} onClose={closeManageTeamModal}>
          <ManageTeamModalContent
            users={usersArray}
            assignments={assignments}
            permissions={permissions}
            videoAuthor={videoAuthor}
            onClose={closeManageTeamModal}
            onSubmit={manageTeam}
            onInvite={handleOpenInvite}
            scenes={scenes}
          />
        </Modal>
        <Modal className={styles.modalWrapper} open={showStoryboardAssistant} onClose={closeStoryboardAssistant}>
          <StoryboardAssistantModal onClose={closeStoryboardAssistant} onRequest={handleAssistantRequest} />
        </Modal>
        {assistantLoading && (
          <div className={styles.assistantLoading}>
            <StoryboardSpinner text={assistantReply == null && t('storyboard.editor.assistant.preparing_response')} onCancel={assistantReply == null ? handleAssistantCanceled : undefined} />
          </div>
        )}
        <Modal open={avatarSelectionModal.show} onClose={() => setAvatarSelectionModal({ show: false })}>
          <AvatarModal
            scenes={scenes}
            sceneId={avatarSelectionModal.sceneId}
            videoId={videoId}
            onSelect={handleAvatarSelection}
            onClose={() => setAvatarSelectionModal({ show: false })}
            userPermissions={userPermissions}
          />
        </Modal>

        <Modal className={styles.warningWrapper} open={requestIsScriptReady?.show} onClose={() => setRequestIsScriptReady({})}>
          <p className={styles.message}
            dangerouslySetInnerHTML={{ __html: t(requestIsScriptReady?.sceneId ? 'clip_organizer.generate_avatar.confirm_script_ready.scene' : 'clip_organizer.generate_avatar.confirm_script_ready', { name: requestIsScriptReady.name }).replace(requestIsScriptReady.name, `<b>${requestIsScriptReady.name}</b>`) }}
          />
          <div className={styles.buttonsContainer}>
            <CustomButton label={t('general.cancel')} variant="secondary" onClick={() => setRequestIsScriptReady({})} />
            <CustomButton label={t('storyboard.invite_modal.other_company_warning.confirm')} onClick={onConfirmScript} variant="primary" />
          </div>
        </Modal>
        <Modal open={isStoryScheduleOpen} onClose={() => setIsStoryScheduleOpen(false)}>
          <StorySchedule
            storySchedule={storySchedule}
            onSubmit={handleSaveSchedule}
            onClose={() => setIsStoryScheduleOpen(false)}
            onRemove={handleRemoveStorySchedule}
            editing={isEditingOptionsEnabled}
            defaultNotification={true}
            allowStartDateExpired={typeof storySchedule?.id == 'string'}
          />
        </Modal>
        <UploadClipModal open={showUploadingModal} onClose={cancelUploading}>
          <UploadClipTab sceneToUploadInfo={uploadClipInfo} onUploadDone={uploadingDone} />
        </UploadClipModal>
        <Modal open={showAvatarModal} onClose={cancelAvatar}>
          <AvatarModal open={showAvatarModal} scenes={scenes?.filter(item => item.id == avatarSceneId)} sceneId={avatarSceneId} onClose={cancelAvatar} onSelect={selectAvatar} />
        </Modal>
        <ClipsProvider>
          <AddClipModal open={showLibraryModal} onClose={cancelSelectFromLibrary}>
            <AddClipContent onClose={cancelSelectFromLibrary} onSave={selectionDone} />
          </AddClipModal>
        </ClipsProvider>
      </div>
      {showMenu.flag && (
        <StoryboardSceneMenu
          customStyles={{ top: menuCoordinates.y, left: menuCoordinates.x }}
          menuRef={menuRef}
          videoAuthor={videoAuthor}
          closeMenu={toggleShowMenu}
          hasScript={menuForScene?.script?.length > 0}
          isProcessing={menuForScene?.processing}
          author={menuForScene?.author}
          status={menuForScene?.status}
          settings={menuForScene?.permissions}
          onDuplicate={onDuplicateCard}
          onDelete={scenes.length > 1 ? onDeleteCard : () => { }}
          onInvite={handleOpenInvite}
          onSendReminder={onSendReminder}
          onCopyLink={copyProjectLink}
          onSettingsChanged={settingChangedFormMenu}
          userPermissions={userPermissions}
          onRequestRevise={onRequestRevise}
          onUseAvatar={onUseAvatarOpen}
        />
      )}
      {storyboardMenu.flag && (
        <StoryboardMenu
          customStyles={{ top: storyboardMenu.top, left: storyboardMenu.left }}
          closeStoryboardMenu={toggleShowStoryboardMenu}
        />
      )}
      <InstallExtensionModal open={isShowExtensionModal} onClose={() => setIsShowExtensionModal(false)} />
    </>
  );
};

export default GeneratedScenesEditor;
